import React, { useState, useEffect } from 'react';
import {
  productionLineService,
  ProductionLineWithCapacity,
  CreateProductionLineData,
  UpdateProductionLineData,
} from '../services/productionLineService';
import { formatDate } from '../utils/date';

interface ProductionLineManagementProps {
  onError: (error: string) => void;
}

const ProductionLineManagement: React.FC<ProductionLineManagementProps> = ({ onError }) => {
  const [productionLines, setProductionLines] = useState<ProductionLineWithCapacity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLine, setEditingLine] = useState<ProductionLineWithCapacity | null>(null);
  const [formData, setFormData] = useState<CreateProductionLineData>({
    name: '',
    description: '',
    defaultWorkers: 6,
    lineType: 'manual',
    displayOrder: 1,
  });
  const [showBulkAssignment, setShowBulkAssignment] = useState(false);
  const [_selectedOrders, _setSelectedOrders] = useState<number[]>([]);
  const [targetLineId, setTargetLineId] = useState<number | null>(null);

  useEffect(() => {
    loadProductionLines();
  }, []);

  const loadProductionLines = async () => {
    try {
      setLoading(true);
      onError('');
      const lines = await productionLineService.getProductionLinesWithWorkload();
      setProductionLines(lines);
    } catch (err: unknown) {
      const error = err as Error;
      onError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      onError('');
      if (editingLine) {
        await productionLineService.updateProductionLine(
          editingLine.id,
          formData as UpdateProductionLineData
        );
      } else {
        await productionLineService.createProductionLine(formData);
      }
      setShowForm(false);
      setEditingLine(null);
      resetForm();
      await loadProductionLines();
    } catch (err: unknown) {
      const error = err as Error;
      onError(error.message);
    }
  };

  const handleEdit = (line: ProductionLineWithCapacity) => {
    setEditingLine(line);
    setFormData({
      name: line.name,
      description: line.description || '',
      defaultWorkers: line.defaultWorkers,
      lineType: line.lineType || (line as any).line_type || 'manual',
      displayOrder: line.displayOrder,
    });
    setShowForm(true);
  };

  const handleDelete = async (lineId: number) => {
    if (
      !confirm(
        'Are you sure you want to delete this production line? This will deactivate it and may affect scheduled orders.'
      )
    ) {
      return;
    }

    try {
      onError('');
      await productionLineService.deleteProductionLine(lineId);
      await loadProductionLines();
    } catch (err: unknown) {
      const error = err as Error;
      onError(error.message);
    }
  };

  const handleActivate = async (lineId: number) => {
    try {
      onError('');
      await productionLineService.activateProductionLine(lineId);
      await loadProductionLines();
    } catch (err: unknown) {
      const error = err as Error;
      onError(error.message);
    }
  };

  const handleDeactivate = async (lineId: number) => {
    try {
      onError('');
      const orderCount = await productionLineService.getActiveOrderCount(lineId);
      const message =
        orderCount > 0
          ? `Deactivating this line will unschedule ${orderCount} order(s). They will remain assigned to this line but won't be scheduled until it is reactivated. Continue?`
          : 'Are you sure you want to deactivate this production line?';

      if (!confirm(message)) return;

      await productionLineService.deactivateProductionLine(lineId);
      await loadProductionLines();
    } catch (err: unknown) {
      const error = err as Error;
      onError(error.message);
    }
  };

  const handleUpdateWorkers = async (lineId: number, workers: number) => {
    try {
      onError('');
      await productionLineService.updateLineWorkers(lineId, workers);
      await loadProductionLines();
    } catch (err: unknown) {
      const error = err as Error;
      onError(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      defaultWorkers: 6,
      lineType: 'manual',
      displayOrder: 1,
    });
    onError('');
  };

  const getStatusBadgeColor = (isActive: boolean) => {
    return isActive ? '#28a745' : '#6c757d';
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization > 100) return '#dc3545'; // Red for over capacity
    if (utilization > 80) return '#ffc107'; // Yellow for high utilization
    return '#28a745'; // Green for normal
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <h3 style={{ margin: 0 }}>Production Line Management</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setEditingLine(null);
              setShowForm(true);
            }}
          >
            + Add Line
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>Loading production lines...</div>
      ) : (
        <div className="table-container">
          <table className="table" style={{ fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ fontSize: '0.8rem' }}>
                <th>Line Name</th>
                <th>Type</th>
                <th>Description</th>
                <th>Workers</th>
                <th>Status</th>
                <th>Current Utilization</th>
                <th>Total Orders</th>
                <th>Display Order</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: '0.875rem' }}>
              {productionLines.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '2rem' }}>
                    No production lines found
                  </td>
                </tr>
              ) : (
                productionLines.map((line) => (
                  <tr key={line.id}>
                    <td>
                      <div>
                        <strong>{line.name}</strong>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>ID: {line.id}</div>
                      </div>
                    </td>
                    <td>
                      <span
                        style={{
                          padding: '0.15rem 0.4rem',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          backgroundColor:
                            (line.lineType || (line as any).line_type) === 'machine'
                              ? '#e3f2fd'
                              : '#f3e5f5',
                          color:
                            (line.lineType || (line as any).line_type) === 'machine'
                              ? '#1565c0'
                              : '#7b1fa2',
                        }}
                      >
                        {line.lineType || (line as any).line_type || 'manual'}
                      </span>
                    </td>
                    <td style={{ maxWidth: '200px' }}>
                      <div
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem',
                        }}
                      >
                        {line.description || '-'}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="number"
                          min="1"
                          max="50"
                          step="1"
                          value={line.defaultWorkers}
                          onChange={(e) => handleUpdateWorkers(line.id, parseInt(e.target.value))}
                          style={{
                            width: '60px',
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                          }}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>workers</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: getStatusBadgeColor(line.isActive),
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                        }}
                      >
                        {line.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div
                          style={{
                            width: '60px',
                            height: '8px',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(line.current_utilization || 0, 100)}%`,
                              height: '100%',
                              backgroundColor: getUtilizationColor(line.current_utilization || 0),
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: '0.75rem',
                            color: getUtilizationColor(line.current_utilization || 0),
                            fontWeight: 'bold',
                          }}
                        >
                          {(line.current_utilization || 0).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td>
                      <div style={{ textAlign: 'center' }}>
                        <strong>{line.total_orders || 0}</strong>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>orders</div>
                      </div>
                    </td>
                    <td>
                      <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                        {line.displayOrder}
                      </div>
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>{formatDate(line.createdAt)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-small"
                          onClick={() => handleEdit(line)}
                          title="Edit Line"
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            color: '#495057',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                          }}
                        >
                          Edit
                        </button>
                        {line.isActive ? (
                          <button
                            className="btn btn-small"
                            onClick={() => handleDeactivate(line.id)}
                            title="Deactivate Line"
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#fff5f5',
                              border: '1px solid #fed7d7',
                              borderRadius: '4px',
                              color: '#c53030',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            className="btn btn-small"
                            onClick={() => handleActivate(line.id)}
                            title="Activate Line"
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#f0fff4',
                              border: '1px solid #c6f6d5',
                              borderRadius: '4px',
                              color: '#2f855a',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                          >
                            Activate
                          </button>
                        )}
                        <button
                          className="btn btn-small"
                          onClick={() => handleDelete(line.id)}
                          title="Delete Line"
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#fff5f5',
                            border: '1px solid #fed7d7',
                            borderRadius: '4px',
                            color: '#c53030',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Production Line Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>{editingLine ? 'Edit Production Line' : 'Add New Production Line'}</h3>
              <button
                className="btn btn-small"
                onClick={() => {
                  setShowForm(false);
                  setEditingLine(null);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="form-group">
                  <label htmlFor="line_name">Line Name *</label>
                  <input
                    type="text"
                    id="line_name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    minLength={2}
                    maxLength={50}
                    placeholder="e.g., PL 3, Line A, Production Line 1"
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="line_description">Description</label>
                  <textarea
                    id="line_description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    maxLength={255}
                    placeholder="Optional description of this production line..."
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      minHeight: '80px',
                    }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="line_type">Line Type *</label>
                  <select
                    id="line_type"
                    value={formData.lineType || 'manual'}
                    onChange={(e) =>
                      setFormData({ ...formData, lineType: e.target.value as 'manual' | 'machine' })
                    }
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="manual">Manual</option>
                    <option value="machine">Machine</option>
                  </select>
                  <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                    Machine lines use a fixed production speed (no worker scaling)
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="line_workers">Default Workers *</label>
                  <input
                    type="number"
                    id="line_workers"
                    value={formData.defaultWorkers}
                    onChange={(e) =>
                      setFormData({ ...formData, defaultWorkers: parseInt(e.target.value) })
                    }
                    required
                    min={1}
                    max={50}
                    step={1}
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                    Number of workers typically assigned to this line (1-50)
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="line_display_order">Display Order *</label>
                  <input
                    type="number"
                    id="line_display_order"
                    value={formData.displayOrder}
                    onChange={(e) =>
                      setFormData({ ...formData, displayOrder: parseInt(e.target.value) })
                    }
                    required
                    min={1}
                    max={100}
                    step={1}
                    style={{
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                    Order in which this line appears in lists and dropdowns
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setEditingLine(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingLine ? 'Update Line' : 'Create Line'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Assignment Modal */}
      {showBulkAssignment && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Bulk Line Assignment</h3>
              <button
                className="btn btn-small"
                onClick={() => {
                  setShowBulkAssignment(false);
                  _setSelectedOrders([]);
                  setTargetLineId(null);
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div
                style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #0ea5e9',
                  borderRadius: '6px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                }}
              >
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#0369a1', fontSize: '1rem' }}>
                  Bulk Assignment Tool
                </h4>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#0369a1' }}>
                  This feature allows you to assign multiple production orders to a specific
                  production line at once. You can select orders from the Production Orders page and
                  then use this tool to assign them all to the same line.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Target Production Line
                </label>
                <select
                  value={targetLineId || ''}
                  onChange={(e) =>
                    setTargetLineId(e.target.value ? parseInt(e.target.value) : null)
                  }
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                  }}
                >
                  <option value="">Select a production line...</option>
                  {productionLines
                    .filter((line) => line.isActive)
                    .map((line) => (
                      <option key={line.id} value={line.id}>
                        {line.name} ({line.defaultWorkers} workers)
                      </option>
                    ))}
                </select>
              </div>

              <div
                style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '6px',
                  padding: '1rem',
                  fontSize: '0.875rem',
                }}
              >
                <strong>How to use:</strong>
                <ol style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                  <li>Go to the Production Orders page</li>
                  <li>Select the orders you want to assign to a line</li>
                  <li>Use the bulk actions menu to assign them to a production line</li>
                  <li>Or use the individual order edit forms to assign orders one by one</li>
                </ol>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowBulkAssignment(false);
                  _setSelectedOrders([]);
                  setTargetLineId(null);
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  // Navigate to production orders page
                  window.location.href = '/production-orders';
                }}
              >
                Go to Production Orders
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionLineManagement;
